import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { toAmericanOdds } from "../utils/odds";
import { defaultMatchweek as getDefaultPickMatchweek } from "../utils/plMatchweek";
import { MatchupHistoryContent } from "../components/MatchupHistoryModal";
import ExpandedTableModal from "../components/ExpandedTableModal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getCurrentMatchweek,
  getLeaderboard,
  getMatchweekSummary,
  getLatestStandings,
  getMyPicks,
  createPick,
  updatePick,
  getPlMatchupHistory,
} from "../api/premierLeague";
import { usePlSeason } from "../hooks/usePlSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const MAX_MATCHWEEK = 38;
const SOCCER_BALL_IMAGE = "/soccerball.png";

// Highlight for a made pick — the team name and the confidence badge share
// this so they read as one thing.
const PICK_HIGHLIGHT_BG = "yellow.100";
const PICK_HIGHLIGHT_FG = "yellow.800";


function normalizeLeaderboardRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

function normalizeFixtures(summaryData) {
  if (Array.isArray(summaryData?.fixtures)) return summaryData.fixtures;
  return [];
}

function normalizeStandingsRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

function normalizeMyPicks(picks) {
  if (!Array.isArray(picks)) return [];

  return picks.map((pick) => ({
    id: pick.id,
    fixture_id: pick.fixture_id,
    season_id: pick.season_id,
    pool_id: pick.pool_id,
    matchweek: pick.matchweek,
    half: pick.half,
    picked_team_id: pick.picked_team_id,
    pickedTeamName: pick.picked_team_name || "-",
    opponent_team_id: pick.opponent_team_id,
    opponentTeamName: pick.opponent_team_name || "-",
    confidence_points: pick.confidence_points,
    points_awarded: pick.points_awarded,
    is_auto_pick: !!pick.is_auto_pick,
    kickoff: pick.kickoff,
    is_final: !!pick.is_final,
    home_team_id: pick.home_team_id,
    away_team_id: pick.away_team_id,
  }));
}

function getTeamName(teamValue) {
  if (!teamValue) return "-";
  if (typeof teamValue === "string") return teamValue;
  return teamValue.name || teamValue.team_name || "-";
}

/** Falls back to the full name for any club without a short_name on record. */
function getTeamShortName(teamValue) {
  if (!teamValue) return "-";
  if (typeof teamValue === "string") return teamValue;
  return teamValue.short_name || getTeamName(teamValue);
}

function getTeamId(teamValue) {
  if (!teamValue) return null;
  if (typeof teamValue === "number") return teamValue;
  return teamValue.id || teamValue.team_id || null;
}

function getFixtureId(fixture, fallbackIndex = 0) {
  return (
    fixture?.fixture_id ||
    fixture?.id ||
    `fixture-${fallbackIndex}-${getTeamName(fixture?.home_team)}-${getTeamName(
      fixture?.away_team
    )}`
  );
}

function getFixtureKickoff(fixture) {
  return (
    fixture?.kickoff ||
    fixture?.kickoff_utc ||
    fixture?.kickoff_at ||
    fixture?.starts_at ||
    fixture?.start_time ||
    fixture?.fixture_date ||
    fixture?.date ||
    fixture?.scheduled_at ||
    fixture?.event_date ||
    fixture?.match_date ||
    fixture?.utc_date ||
    fixture?.fixture?.date ||
    fixture?.fixture?.kickoff ||
    fixture?.fixture?.kickoff_at ||
    null
  );
}

function formatFixtureDateTime(fixture) {
  const raw = getFixtureKickoff(fixture);
  if (!raw) return "Date/time TBD";

  let normalized = raw;
  if (typeof raw === "string") {
    const hasTimezone = raw.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(raw);
    if (!hasTimezone) {
      // Treat naive datetimes as UTC (they are stored in UTC in the DB)
      normalized = raw.trim().replace(" ", "T") + "Z";
    }
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "Date/time TBD";

  const formatted = date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${formatted} ET`;
}

function getSeasonHalf(matchweek) {
  return matchweek >= 1 && matchweek <= 19 ? 1 : 2;
}


function getStandingForTeam(standingsRows, teamName) {
  return standingsRows.find(
    (row) => (row?.team_name || "").toLowerCase() === (teamName || "").toLowerCase()
  );
}

function getUsedTeamIdsForHalf(history, half) {
  return new Set(
    history
      .filter((item) => item.half === half && item.picked_team_id != null)
      .map((item) => item.picked_team_id)
  );
}

function getPickedAgainstCountsForHalf(history, half) {
  const counts = {};
  history
    .filter((item) => item.half === half && item.opponent_team_id != null)
    .forEach((item) => {
      counts[item.opponent_team_id] = (counts[item.opponent_team_id] || 0) + 1;
    });
  return counts;
}

/**
 * Whether the server will accept a pick on this particular fixture.
 *
 * Decided per fixture, not per matchweek: an early kickoff must not close the
 * ones still to come. The API sends can_pick_this_fixture (no pick yet, week
 * open, this fixture unstarted) and can_edit (this is your pick and it hasn't
 * started) — the same rules the save endpoint enforces, so the grid can't
 * offer something that would be refused.
 *
 * Once your pick locks, every fixture returns false and the whole grid greys
 * out, which is correct: you get one pick per matchweek and yours is spent.
 */
function isFixtureBlocked(fixture, summaryData) {
  const st = fixture?.status;
  if (!st) return false;  // Unrecognised shape — leave the fixture alone.

  // No pick yet and this one hasn't started, or this is your pick and it
  // hasn't started.
  if (st.can_pick_this_fixture === true || st.can_edit === true) return false;

  // Moving an existing pick to a different fixture. user.can_edit means your
  // current pick is still unlocked, so it may be moved anywhere that hasn't
  // kicked off — the save endpoint allows this, and only the fixture's own
  // kickoff stops it.
  if (summaryData?.user?.can_edit === true && st.is_locked !== true) return false;

  return true;
}

function isFixtureLocked(fixture, summaryData) {
  if (!fixture) return true;
  if (fixture?.is_final) return true;

  const kickoff = getFixtureKickoff(fixture);
  if (kickoff) {
    let normalized = kickoff;
    if (typeof kickoff === "string") {
      const hasTimezone = kickoff.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(kickoff);
      if (!hasTimezone) normalized = `${kickoff}Z`;
    }
    const kickoffDate = new Date(normalized);
    if (!Number.isNaN(kickoffDate.getTime()) && kickoffDate.getTime() <= Date.now()) {
      return true;
    }
  }

  if (summaryData?.matchweek_closed === true) return true;
  if (summaryData?.is_closed === true) return true;
  if (summaryData?.state?.closed === true) return true;

  return false;
}

function renderFixtureLabel(fixture, selectedTeamName) {
  const homeTeam = fixture?.home_team;
  const awayTeam = fixture?.away_team;
  const home = getTeamName(homeTeam);
  const away = getTeamName(awayTeam);
  const homeShort = homeTeam?.short_name || home;
  const awayShort = awayTeam?.short_name || away;
  const homeLogo = homeTeam?.logo_url;
  const awayLogo = awayTeam?.logo_url;

  const homeSelected =
    selectedTeamName && selectedTeamName.toLowerCase() === home.toLowerCase();
  const awaySelected =
    selectedTeamName && selectedTeamName.toLowerCase() === away.toLowerCase();

  return (
    <>
      {/* Desktop: side-by-side logos with short names underneath, "vs." in middle */}
      <Flex justify="center" align="center" gap={1} w="100%" display={{ base: "none", md: "flex" }}>
        <VStack spacing={0} align="center">
          {homeLogo && <Image src={homeLogo} boxSize="50px" objectFit="contain" />}
          <Box as="span" bg={homeSelected ? PICK_HIGHLIGHT_BG : "transparent"} px={1} borderRadius="sm" fontWeight="bold" fontSize="xs" color="black" lineHeight="1.3" textAlign="center">
            {homeShort}
          </Box>
        </VStack>
        <Box as="span" fontSize="sm" color="gray.700" fontWeight="bold" px={1}>vs.</Box>
        <VStack spacing={0} align="center">
          {awayLogo && <Image src={awayLogo} boxSize="50px" objectFit="contain" />}
          <Box as="span" bg={awaySelected ? PICK_HIGHLIGHT_BG : "transparent"} px={1} borderRadius="sm" fontWeight="bold" fontSize="xs" color="black" lineHeight="1.3" textAlign="center">
            {awayShort}
          </Box>
        </VStack>
      </Flex>
      {/* Mobile: side-by-side logos with short names underneath, "vs." in middle */}
      <Flex justify="center" align="center" gap={1} w="100%" display={{ base: "flex", md: "none" }}>
        <VStack spacing={0} align="center">
          {homeLogo && <Image src={homeLogo} boxSize="40px" objectFit="contain" />}
          <Box as="span" bg={homeSelected ? PICK_HIGHLIGHT_BG : "transparent"} px={1} borderRadius="sm" fontWeight="bold" fontSize="xs" color="black" lineHeight="1.3" textAlign="center">
            {homeShort}
          </Box>
        </VStack>
        <Box as="span" fontSize="xs" color="gray.700" fontWeight="bold" px={1}>vs.</Box>
        <VStack spacing={0} align="center">
          {awayLogo && <Image src={awayLogo} boxSize="40px" objectFit="contain" />}
          <Box as="span" bg={awaySelected ? PICK_HIGHLIGHT_BG : "transparent"} px={1} borderRadius="sm" fontWeight="bold" fontSize="xs" color="black" lineHeight="1.3" textAlign="center">
            {awayShort}
          </Box>
        </VStack>
      </Flex>
    </>
  );
}

/**
 * How a pick's result reads in the history grid.
 *
 * Once scored it shows the points won or lost, signed and coloured; before
 * that it falls back to the confidence staked, so an upcoming week still shows
 * what is riding on it.
 */
function historyResult(item, neutralColor) {
  const pts = item.points_awarded;
  if (pts == null) {
    return {
      label: item.confidence_points != null ? ` (${item.confidence_points})` : "",
      color: neutralColor,
    };
  }
  if (pts > 0) return { label: ` (+${pts})`, color: "green.600" };
  if (pts < 0) return { label: ` (${pts})`, color: "red.600" };
  return { label: " (0)", color: neutralColor };
}

function HistoryGrid({ items, topBarBlue }) {
  return (
    <Grid templateColumns={{ base: "1fr", md: "repeat(5, minmax(0, 1fr))" }} gap={3}>
      {items.map((item) => (
        <GridItem
          key={`history-${item.matchweek}`}
          bg="gray.50"
          border={item.is_auto_pick ? "2px solid" : "1px solid"}
          borderColor={item.is_auto_pick ? "red.500" : "gray.200"}
          borderRadius="md"
          p={2}
          minH="44px"
          display="flex"
          alignItems="center"
        >
          {(() => {
            const res = historyResult(item, topBarBlue);
            return (
              <Text fontSize="sm" fontWeight="bold" color={res.color} noOfLines={1}>
                MW{item.matchweek} - {item.pickedTeamName || "-"}{res.label}
              </Text>
            );
          })()}
        </GridItem>
      ))}
    </Grid>
  );
}

function usePremierLeaguePickPageData(poolId, seasonId) {
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [currentMwMeta, setCurrentMwMeta] = useState(null);
  const [selectedMatchweek, setSelectedMatchweek] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [standingsRows, setStandingsRows] = useState([]);
  const [pickHistory, setPickHistory] = useState([]);
  const [savedPicksByFixture, setSavedPicksByFixture] = useState({});
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingMatchweek, setLoadingMatchweek] = useState(false);
  const [pageError, setPageError] = useState("");

  const buildSavedPicksMap = useCallback((normalizedHistory, matchweek) => {
    const picksMap = {};
    normalizedHistory
      .filter((pick) => pick.matchweek === matchweek)
      .forEach((pick) => {
        picksMap[pick.fixture_id] = {
          pick_id: pick.id,
          picked_team_id: pick.picked_team_id,
          pickedTeamName: pick.pickedTeamName,
          confidence_points: pick.confidence_points,
        };
      });
    return picksMap;
  }, []);

  const loadPage = useCallback(
    async (matchweekOverride = null) => {
      if (!poolId || Number.isNaN(poolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      if (!seasonId) return;

      try {
        setLoadingPage(true);
        setPageError("");

        const [leaderboard, matchweekMeta, latestStandings, myPicksRaw] =
          await Promise.all([
            getLeaderboard(poolId, seasonId).catch(() => []),
            getCurrentMatchweek(seasonId).catch(() => null),
            getLatestStandings(seasonId).then(normalizeStandingsRows).catch(() => []),
            getMyPicks(poolId, seasonId).catch(() => []),
          ]);

        setLeaderboardRows(normalizeLeaderboardRows(leaderboard));
        setCurrentMwMeta(matchweekMeta);
        setStandingsRows(latestStandings);

        if (!matchweekMeta) {
          // Season not started yet — not an error
          setLoadingPage(false);
          return;
        }

        const mw = matchweekOverride || getDefaultPickMatchweek(matchweekMeta);
        const summary = await getMatchweekSummary(poolId, mw, seasonId).catch(() => null);
        const normalizedHistory = normalizeMyPicks(myPicksRaw);
        const picksMap = buildSavedPicksMap(normalizedHistory, mw);

        setSelectedMatchweek(mw);
        setSummaryData(summary);
        setPickHistory(normalizedHistory);
        setSavedPicksByFixture(picksMap);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to load Premier League pick page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    },
    [poolId, seasonId, buildSavedPicksMap]
  );

  const changeMatchweek = useCallback(
    async (nextMw) => {
      if (!nextMw || nextMw < 1 || nextMw > MAX_MATCHWEEK) return;
      if (!poolId || Number.isNaN(poolId)) return;

      try {
        setLoadingMatchweek(true);
        setPageError("");

        const [summary, myPicksRaw] = await Promise.all([
          getMatchweekSummary(poolId, nextMw, seasonId),
          getMyPicks(poolId, seasonId),
        ]);

        const normalizedHistory = normalizeMyPicks(myPicksRaw);
        const picksMap = buildSavedPicksMap(normalizedHistory, nextMw);

        setSelectedMatchweek(nextMw);
        setSummaryData(summary);
        setPickHistory(normalizedHistory);
        setSavedPicksByFixture(picksMap);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to load matchweek.";
        setPageError(typeof msg === "string" ? msg : "Unable to load matchweek.");
      } finally {
        setLoadingMatchweek(false);
      }
    },
    [poolId, seasonId, buildSavedPicksMap]
  );

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  return {
    leaderboardRows,
    currentMwMeta,
    selectedMatchweek,
    setSelectedMatchweek,
    summaryData,
    standingsRows,
    pickHistory,
    savedPicksByFixture,
    loadingPage,
    loadingMatchweek,
    pageError,
    setPageError,
    loadPage,
    changeMatchweek,
  };
}

export default function PremierLeaguePickPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { poolId } = useParams();
  const numericPoolId = Number(poolId);
  const location = useLocation();
  const { seasonId: _hookSeasonId } = usePlSeason();
  const SEASON_ID = location.state?.seasonId ?? _hookSeasonId;

  const {
    isOpen: isPickModalOpen,
    onOpen: onPickModalOpen,
    onClose: onPickModalClose,
  } = useDisclosure();

  const {
    isOpen: isExpandedTableOpen,
    onOpen: onExpandedTableOpen,
    onClose: onExpandedTableClose,
  } = useDisclosure();

  const {
    leaderboardRows,
    currentMwMeta,
    selectedMatchweek,
    summaryData,
    standingsRows,
    pickHistory,
    savedPicksByFixture,
    loadingPage,
    loadingMatchweek,
    pageError,
    setPageError,
    loadPage,
    changeMatchweek,
  } = usePremierLeaguePickPageData(numericPoolId, SEASON_ID);

  const [activeFixture, setActiveFixture] = useState(null);
  const [modalWinnerTeamId, setModalWinnerTeamId] = useState(null);
  const [modalConfidencePoints, setModalConfidencePoints] = useState(3);
  const [savingPick, setSavingPick] = useState(false);
  // Full club names wrap the pick modal's title onto a second line on a phone,
  // which pushes the Save button out of line with the close button.
  const shortModalTitle = useBreakpointValue({ base: true, md: false }) ?? false;
  const modalTeamName = shortModalTitle ? getTeamShortName : getTeamName;
  const [showFirstHalf, setShowFirstHalf] = useState(true);
  const [showSecondHalf, setShowSecondHalf] = useState(true);

  const username = user?.username || "Player";

  const fixtures = useMemo(() => normalizeFixtures(summaryData), [summaryData]);


  const currentUserLeaderboardRow = useMemo(() => {
    return (
      leaderboardRows.find((row) => {
        const rowName = (row?.user || row?.username || "")
          .toString()
          .trim()
          .toLowerCase();
        return rowName === username.trim().toLowerCase();
      }) || null
    );
  }, [leaderboardRows, username]);

  const currentHalf = useMemo(
    () => getSeasonHalf(selectedMatchweek || 1),
    [selectedMatchweek]
  );

  useEffect(() => {
    if (currentHalf === 1) {
      setShowFirstHalf(true);
      setShowSecondHalf(false);
    } else {
      setShowFirstHalf(false);
      setShowSecondHalf(true);
    }
  }, [currentHalf]);

  const usedTeamIdsThisHalf = useMemo(
    () => getUsedTeamIdsForHalf(pickHistory, currentHalf),
    [pickHistory, currentHalf]
  );

  const pickedAgainstCountsThisHalf = useMemo(
    () => getPickedAgainstCountsForHalf(pickHistory, currentHalf),
    [pickHistory, currentHalf]
  );

  const firstHalfHistory = useMemo(
    () =>
      Array.from({ length: 19 }, (_, i) => {
        const mw = i + 1;
        return (
          pickHistory.find((item) => item.matchweek === mw) || {
            matchweek: mw,
            pickedTeamName: "-",
            confidence_points: null,
            is_auto_pick: false,
          }
        );
      }),
    [pickHistory]
  );

  const secondHalfHistory = useMemo(
    () =>
      Array.from({ length: 19 }, (_, i) => {
        const mw = i + 20;
        return (
          pickHistory.find((item) => item.matchweek === mw) || {
            matchweek: mw,
            pickedTeamName: "-",
            confidence_points: null,
            is_auto_pick: false,
          }
        );
      }),
    [pickHistory]
  );

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function openPickModal(fixture, fallbackIndex) {
    if (isFixtureLocked(fixture, summaryData)) return;

    const fixtureId = getFixtureId(fixture, fallbackIndex);
    const existingPick = savedPicksByFixture[fixtureId];

    setActiveFixture(fixture);
    setModalWinnerTeamId(existingPick?.picked_team_id || null);
    setModalConfidencePoints(existingPick?.confidence_points || 3);
    onPickModalOpen();
  }

  function getSelectionRuleState(fixture, teamId) {
    const homeId = getTeamId(fixture?.home_team) ?? fixture?.home_team_id;
    const awayId = getTeamId(fixture?.away_team) ?? fixture?.away_team_id;
    const homeName = getTeamName(fixture?.home_team);
    const awayName = getTeamName(fixture?.away_team);

    const opponentId = teamId === homeId ? awayId : homeId;
    const opponentName = teamId === homeId ? awayName : homeName;

    const alreadyPickedThisHalf = usedTeamIdsThisHalf.has(teamId);
    const opponentPickedAgainstCount = pickedAgainstCountsThisHalf[opponentId] || 0;
    const opponentLockedByAgainstRule = opponentPickedAgainstCount >= 4;

    return {
      disabled: alreadyPickedThisHalf || opponentLockedByAgainstRule,
      alreadyPickedThisHalf,
      opponentLockedByAgainstRule,
      opponentName,
    };
  }

  function getExistingPickForSelectedMatchweek() {
    return pickHistory.find((pick) => pick.matchweek === selectedMatchweek) || null;
  }

  async function handleSaveModalPick() {
    if (!activeFixture || !modalWinnerTeamId) return;
    if (!numericPoolId || Number.isNaN(numericPoolId)) return;

    const fixtureId = activeFixture?.fixture_id || activeFixture?.id;
    const existingPickForMatchweek = getExistingPickForSelectedMatchweek();

    try {
      setSavingPick(true);
      setPageError("");

      const payload = {
        fixture_id: fixtureId,
        picked_team_id: modalWinnerTeamId,
        confidence_points: modalConfidencePoints,
      };

      if (existingPickForMatchweek?.id) {
        await updatePick(numericPoolId, existingPickForMatchweek.id, payload, SEASON_ID);
      } else {
        await createPick(numericPoolId, payload, SEASON_ID);
      }

      onPickModalClose();
      await loadPage(selectedMatchweek);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        setPageError(detail);
      } else if (detail?.message) {
        setPageError(detail.message);
      } else {
        setPageError("Unable to save pick.");
      }
    } finally {
      setSavingPick(false);
    }
  }

  function getSavedPickForFixture(fixture, fallbackIndex) {
    const fixtureId = getFixtureId(fixture, fallbackIndex);
    return savedPicksByFixture[fixtureId] || null;
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading picks page...</Text>
        </Flex>
      </Box>
    );
  }

  const activeHomeTeam = getTeamName(activeFixture?.home_team);
  const activeAwayTeam = getTeamName(activeFixture?.away_team);
  const activeHomeTeamId = getTeamId(activeFixture?.home_team) ?? activeFixture?.home_team_id;
  const activeAwayTeamId = getTeamId(activeFixture?.away_team) ?? activeFixture?.away_team_id;
  const activeHomeLogo = activeFixture?.home_team?.logo_url;
  const activeAwayLogo = activeFixture?.away_team?.logo_url;

  const activeHomeStanding = getStandingForTeam(standingsRows, activeHomeTeam);
  const activeAwayStanding = getStandingForTeam(standingsRows, activeAwayTeam);

  const activeHomeRule = activeFixture
    ? getSelectionRuleState(activeFixture, activeHomeTeamId)
    : null;
  const activeAwayRule = activeFixture
    ? getSelectionRuleState(activeFixture, activeAwayTeamId)
    : null;

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="PL PICK ‘EM PICKS"
        onBack={() => navigate(`/premier-league/current/${poolId}`, { state: { seasonId: SEASON_ID } })}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={8}>
        {pageError ? (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />
            {pageError}
          </Alert>
        ) : null}

        {!pageError && !currentMwMeta && (
          <Alert status="info" borderRadius="md" mb={6} color={TOP_BAR_BLUE}>
            <AlertIcon />
            Data not yet available — picks will open once the season begins.
          </Alert>
        )}

        <Flex justify="flex-start" mb={4}>
          <Button
            size="sm"
            bg="white"
            color={TOP_BAR_BLUE}
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate(`/premier-league/current/${poolId}`, { state: { seasonId: SEASON_ID } })}
          >
            Return to Leaderboard
          </Button>
        </Flex>

        {/* Confirms this matchweek's pick without opening the modal again. */}
        {(() => {
          const saved = getExistingPickForSelectedMatchweek();
          if (!saved) return null;
          return (
            <Alert status="success" borderRadius="md" color="green.900" mb={4}>
              <AlertIcon />
              <Text>
                MW{saved.matchweek}: <strong>{saved.pickedTeamName}</strong> for{" "}
                <strong>{saved.confidence_points}</strong> point
                {saved.confidence_points === 1 ? "" : "s"}
                {saved.is_auto_pick ? " (autopick)" : ""}
              </Text>
            </Alert>
          );
        })()}

        <Flex align="stretch" gap={{ base: 3, md: 6 }} flexWrap="wrap" mb={6}>
          <Box
            flex="2"
            minW={{ base: "100%", md: "680px" }}
            maxW={{ base: "100%", md: "calc(100% - 366px)" }}
            border="2px solid white"
            borderRadius="lg"
            px={5}
            py={4}
            bg="whiteAlpha.50"
          >
            <Text
              fontFamily="'Orbitron', sans-serif"
              fontSize={{ base: "18px", md: "30px" }}
              letterSpacing="2px"
              fontWeight="600"
              color="white"
            >
              {username}
            </Text>
          </Box>

          <Box flex="1" minW={{ base: "100%", md: "360px" }} maxW={{ base: "100%", md: "360px" }}>
            <Grid templateColumns="repeat(3, 1fr)" gap={4} h="100%">
              <Box
                border="2px solid white"
                borderRadius="lg"
                px={4}
                py={3}
                bg="whiteAlpha.50"
                textAlign="center"
              >
                <Text fontSize="xs" color="whiteAlpha.800" textTransform="uppercase">
                  Points
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {currentUserLeaderboardRow?.points ?? 0}
                </Text>
              </Box>

              <Box
                border="2px solid white"
                borderRadius="lg"
                px={4}
                py={3}
                bg="whiteAlpha.50"
                textAlign="center"
              >
                <Text fontSize="xs" color="whiteAlpha.800" textTransform="uppercase">
                  GD
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {currentUserLeaderboardRow?.goal_differential ?? 0}
                </Text>
              </Box>

              <Box
                border="2px solid white"
                borderRadius="lg"
                px={4}
                py={3}
                bg="whiteAlpha.50"
                textAlign="center"
              >
                <Text fontSize="xs" color="whiteAlpha.800" textTransform="uppercase">
                  Rank
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {currentUserLeaderboardRow?.rank ?? "-"}
                </Text>
              </Box>
            </Grid>
          </Box>
        </Flex>

        <Flex align="stretch" gap={{ base: 3, md: 6 }} flexWrap="wrap">
          <Box
            flex="2"
            minW={{ base: "100%", md: "680px" }}
            maxW={{ base: "100%", md: "calc(100% - 366px)" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={{ base: 4, md: 6 }}
          >
            <VStack align="stretch" spacing={6}>
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
                <HStack spacing={3}>
                  <Button
                    size="sm"
                    onClick={() => changeMatchweek((selectedMatchweek || 1) - 1)}
                    isDisabled={(selectedMatchweek || 1) <= 1 || loadingMatchweek}
                  >
                    ◀
                  </Button>

                  <Heading size="md" color={TOP_BAR_BLUE}>
                    Matchweek {selectedMatchweek || currentMwMeta?.current_matchweek || 1} Pick
                  </Heading>

                  <Button
                    size="sm"
                    onClick={() => changeMatchweek((selectedMatchweek || 1) + 1)}
                    isDisabled={loadingMatchweek || (selectedMatchweek || 1) >= MAX_MATCHWEEK}
                  >
                    ▶
                  </Button>
                </HStack>

                <Text fontWeight="bold" color={TOP_BAR_BLUE}>
                  Select Matchup to Make Pick
                </Text>
              </Flex>

              {loadingMatchweek ? (
                <Flex align="center" justify="center" py={12}>
                  <Spinner />
                </Flex>
              ) : fixtures.length > 0 ? (
                <>
                  {/* Desktop: explicit 3-2-3-2 staggered rows */}
                  <Box display={{ base: "none", md: "block" }}>
                    {(() => {
                      const rows = [];
                      let i = 0;
                      const pattern = [3, 2];
                      let p = 0;
                      while (i < fixtures.length) {
                        const n = Math.min(pattern[p % pattern.length], fixtures.length - i);
                        rows.push({ items: fixtures.slice(i, i + n), startIdx: i });
                        i += n;
                        p++;
                      }
                      return rows.map(({ items, startIdx }, rowIdx) => (
                        <Flex key={rowIdx} justify="center" gap={6} mb={rowIdx < rows.length - 1 ? 6 : 0}>
                          {items.map((fixture, localIdx) => {
                            const absoluteIndex = startIdx + localIdx;
                            const savedPick = getSavedPickForFixture(fixture, absoluteIndex);
                            const fixtureDateText = formatFixtureDateTime(fixture);
                            const locked = isFixtureLocked(fixture, summaryData);
                            const homeId = getTeamId(fixture?.home_team) ?? fixture?.home_team_id;
                            const awayId = getTeamId(fixture?.away_team) ?? fixture?.away_team_id;
                            const bothBlocked = !locked &&
                              getSelectionRuleState(fixture, homeId).disabled &&
                              getSelectionRuleState(fixture, awayId).disabled;
                            const fullyBlocked = locked || bothBlocked || isFixtureBlocked(fixture, summaryData);
                            return (
                              <Box
                                key={`fixture-d-${absoluteIndex}`}
                                display="flex"
                                justifyContent="center"
                                cursor={fullyBlocked ? "not-allowed" : "pointer"}
                                onClick={() => !fullyBlocked && openPickModal(fixture, absoluteIndex)}
                                opacity={fullyBlocked && !savedPick ? 0.45 : 1}
                              >
                                <Box
                                  width="160px"
                                  height="160px"
                                  borderRadius="full"
                                  border={`3px solid ${savedPick ? "#d69e2e" : TOP_BAR_BLUE}`}
                                  bg="white"
                                  position="relative"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  boxShadow="md"
                                  px={3}
                                  textAlign="center"
                                  overflow="hidden"
                                >
                                  <Box
                                    position="absolute"
                                    inset="0"
                                    borderRadius="full"
                                    backgroundImage={`url(${SOCCER_BALL_IMAGE})`}
                                    backgroundPosition="center"
                                    backgroundRepeat="no-repeat"
                                    backgroundSize="125%"
                                    opacity={0.15}
                                  />
                                  <VStack spacing={2} position="relative" zIndex={1}>
                                    {renderFixtureLabel(fixture, savedPick?.pickedTeamName || null)}
                                    <Text fontSize="xs" color="gray.600">{fixtureDateText}</Text>
                                    {savedPick && (
                                      <Badge bg={PICK_HIGHLIGHT_BG} color={PICK_HIGHLIGHT_FG} fontSize="0.5rem">
                                        Your pick · {savedPick.confidence_points}
                                      </Badge>
                                    )}
                                  </VStack>
                                </Box>
                              </Box>
                            );
                          })}
                        </Flex>
                      ));
                    })()}
                  </Box>

                  {/* Mobile: 2-per-row */}
                  <Flex display={{ base: "flex", md: "none" }} flexWrap="wrap" gap={3} justify="center">
                    {fixtures.map((fixture, absoluteIndex) => {
                      const savedPick = getSavedPickForFixture(fixture, absoluteIndex);
                      const locked = isFixtureLocked(fixture, summaryData);
                      const homeId = getTeamId(fixture?.home_team) ?? fixture?.home_team_id;
                      const awayId = getTeamId(fixture?.away_team) ?? fixture?.away_team_id;
                      const bothBlocked = !locked &&
                        getSelectionRuleState(fixture, homeId).disabled &&
                        getSelectionRuleState(fixture, awayId).disabled;
                      const fullyBlocked = locked || bothBlocked || isFixtureBlocked(fixture, summaryData);
                      return (
                        <Box
                          key={`fixture-m-${absoluteIndex}`}
                          w="calc(50% - 6px)"
                          display="flex"
                          justifyContent="center"
                          cursor={fullyBlocked ? "not-allowed" : "pointer"}
                          onClick={() => !fullyBlocked && openPickModal(fixture, absoluteIndex)}
                          opacity={fullyBlocked && !savedPick ? 0.45 : 1}
                        >
                          <Box
                            width="130px"
                            height="130px"
                            borderRadius="full"
                            border={`3px solid ${savedPick ? "#d69e2e" : TOP_BAR_BLUE}`}
                            bg="white"
                            position="relative"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            boxShadow="md"
                            px={3}
                            textAlign="center"
                            overflow="hidden"
                          >
                            <Box
                              position="absolute"
                              inset="0"
                              borderRadius="full"
                              backgroundImage={`url(${SOCCER_BALL_IMAGE})`}
                              backgroundPosition="center"
                              backgroundRepeat="no-repeat"
                              backgroundSize="125%"
                              opacity={0.15}
                            />
                            <VStack spacing={2} position="relative" zIndex={1}>
                              {renderFixtureLabel(fixture, savedPick?.pickedTeamName || null)}
                              {savedPick && (
                                <Badge bg={PICK_HIGHLIGHT_BG} color={PICK_HIGHLIGHT_FG} fontSize="0.5rem">
                                  Your pick · {savedPick.confidence_points}
                                </Badge>
                              )}
                            </VStack>
                          </Box>
                        </Box>
                      );
                    })}
                  </Flex>
                </>
              ) : (
                <Text color="gray.600">No fixtures found for this matchweek.</Text>
              )}
            </VStack>
          </Box>

          <Box
            flex="1"
            minW={{ base: "100%", md: "360px" }}
            maxW={{ base: "100%", md: "360px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={{ base: 4, md: 6 }}
          >
            <VStack align="stretch" spacing={4}>
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
                <Heading size="md" color={TOP_BAR_BLUE} lineHeight="1">
                  Current Table
                </Heading>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor={TOP_BAR_BLUE}
                  color={TOP_BAR_BLUE}
                  onClick={onExpandedTableOpen}
                  _hover={{ bg: "gray.100" }}
                >
                  Expanded Stats
                </Button>
              </Flex>

              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Rank</Th>
                      <Th>Team</Th>
                      <Th isNumeric>Points</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {standingsRows.length > 0 ? (
                      standingsRows.map((row) => (
                        <Tr key={`${row.team_id}-${row.rank}`}>
                          <Td>{row.rank ?? "-"}</Td>
                          <Td>
                            <HStack spacing={2} align="center">
                              {row.logo_url && (
                                <Image src={row.logo_url} boxSize="18px" objectFit="contain" flexShrink={0} />
                              )}
                              <span>{row.team_name || "-"}</span>
                            </HStack>
                          </Td>
                          <Td isNumeric>{row.points ?? 0}</Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={3}>
                          <Text color="gray.600">
                            No standings available for this season yet.
                          </Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </Box>
        </Flex>

        <VStack align="stretch" spacing={6} mt={8}>
          <Box
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={{ base: 4, md: 6 }}
          >
            <VStack align="stretch" spacing={5}>
              <Heading size="md" color={TOP_BAR_BLUE}>
                Pick History
              </Heading>

              <Box border="1px solid" borderColor="gray.200" borderRadius="md">
                <Button
                  width="100%"
                  variant="ghost"
                  justifyContent="space-between"
                  onClick={() => setShowFirstHalf((v) => !v)}
                  rightIcon={
                    <ChevronDownIcon
                      transform={showFirstHalf ? "rotate(0deg)" : "rotate(-90deg)"}
                      transition="transform 0.2s ease"
                    />
                  }
                  borderRadius="md"
                >
                  1st Half
                </Button>
                {showFirstHalf && (
                  <Box p={4} pt={0}>
                    <HistoryGrid items={firstHalfHistory} topBarBlue={TOP_BAR_BLUE} />
                  </Box>
                )}
              </Box>

              <Box border="1px solid" borderColor="gray.200" borderRadius="md">
                <Button
                  width="100%"
                  variant="ghost"
                  justifyContent="space-between"
                  onClick={() => setShowSecondHalf((v) => !v)}
                  rightIcon={
                    <ChevronDownIcon
                      transform={showSecondHalf ? "rotate(0deg)" : "rotate(-90deg)"}
                      transition="transform 0.2s ease"
                    />
                  }
                  borderRadius="md"
                >
                  2nd Half
                </Button>
                {showSecondHalf && (
                  <Box p={4} pt={0}>
                    <HistoryGrid items={secondHalfHistory} topBarBlue={TOP_BAR_BLUE} />
                  </Box>
                )}
              </Box>

              <Text fontSize="sm" color="red.600">
                Red highlight is an autopick
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Modal isOpen={isPickModalOpen} onClose={onPickModalClose} isCentered size="3xl"
             scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent
          bg="white"
          color="black"
          border={`3px solid ${TOP_BAR_BLUE}`}
          borderRadius="lg"
          maxW="900px"
          w="calc(100vw - 32px)"
        >
          <ModalHeader color={TOP_BAR_BLUE} pr={14}>
            <Flex align="center" justify="space-between" gap={3}>
              <Box>
                {activeFixture
                  ? `${modalTeamName(activeFixture?.home_team)} vs. ${modalTeamName(
                      activeFixture?.away_team
                    )}`
                  : "Match Pick"}
              </Box>
              {activeFixture && (
                <Button
                  size="sm"
                  flexShrink={0}
                  bg="red.600"
                  color="white"
                  _hover={{ bg: "red.700" }}
                  onClick={handleSaveModalPick}
                  isDisabled={!modalWinnerTeamId || savingPick}
                >
                  {savingPick ? "Saving..." : "Save Pick"}
                </Button>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />

          <ModalBody pb={6}>
            {activeFixture ? (
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Text fontSize="sm" color="gray.600">
                    {formatFixtureDateTime(activeFixture)}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={3}>
                    Select Winner
                  </Text>

                  <Flex gap={3} flexWrap="wrap" align="flex-start">
                    <Box flex="1" minW={{ base: "100%", md: "180px" }}>
                    <Text fontSize="0.6rem" color="gray.500" textTransform="uppercase" mb={1}>
                      Home
                    </Text>
                    <Button
                      w="100%"
                      variant={modalWinnerTeamId === activeHomeTeamId ? "solid" : "outline"}
                      bg={modalWinnerTeamId === activeHomeTeamId ? TOP_BAR_BLUE : "white"}
                      color={modalWinnerTeamId === activeHomeTeamId ? "white" : TOP_BAR_BLUE}
                      borderColor={TOP_BAR_BLUE}
                      onClick={() => setModalWinnerTeamId(activeHomeTeamId)}
                      isDisabled={activeHomeRule?.disabled}
                    >
                      <HStack spacing={2} justify="center">
                        {activeHomeLogo && (
                          <Image src={activeHomeLogo} boxSize="20px" objectFit="contain" />
                        )}
                        <span>{activeHomeTeam}</span>
                      </HStack>
                    </Button>
                    </Box>

                    <Box flex="1" minW={{ base: "100%", md: "180px" }}>
                    <Text fontSize="0.6rem" color="gray.500" textTransform="uppercase" mb={1}>
                      Away
                    </Text>
                    <Button
                      w="100%"
                      variant={modalWinnerTeamId === activeAwayTeamId ? "solid" : "outline"}
                      bg={modalWinnerTeamId === activeAwayTeamId ? TOP_BAR_BLUE : "white"}
                      color={modalWinnerTeamId === activeAwayTeamId ? "white" : TOP_BAR_BLUE}
                      borderColor={TOP_BAR_BLUE}
                      onClick={() => setModalWinnerTeamId(activeAwayTeamId)}
                      isDisabled={activeAwayRule?.disabled}
                    >
                      <HStack spacing={2} justify="center">
                        {activeAwayLogo && (
                          <Image src={activeAwayLogo} boxSize="20px" objectFit="contain" />
                        )}
                        <span>{activeAwayTeam}</span>
                      </HStack>
                    </Button>
                    </Box>
                  </Flex>

                  <VStack align="stretch" spacing={2} mt={3}>
                    {activeHomeRule?.alreadyPickedThisHalf ? (
                      <Text fontSize="sm" color="red.600">
                        {activeHomeTeam} is unavailable because you already picked them in this half of the season.
                      </Text>
                    ) : null}

                    {activeAwayRule?.alreadyPickedThisHalf ? (
                      <Text fontSize="sm" color="red.600">
                        {activeAwayTeam} is unavailable because you already picked them in this half of the season.
                      </Text>
                    ) : null}

                    {activeHomeRule?.opponentLockedByAgainstRule ? (
                      <Text fontSize="sm" color="red.600">
                        {activeHomeTeam} is unavailable because you already picked against {activeAwayTeam} 4 times in this half.
                      </Text>
                    ) : null}

                    {activeAwayRule?.opponentLockedByAgainstRule ? (
                      <Text fontSize="sm" color="red.600">
                        {activeAwayTeam} is unavailable because you already picked against {activeHomeTeam} 4 times in this half.
                      </Text>
                    ) : null}
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={4}>
                    Confidence Points: {modalConfidencePoints}
                  </Text>

                  <Box px={2}>
                    <Box position="relative" pt={6}>
                      {[1, 2, 3, 4, 5].map((num) => (
                        <Text
                          key={`label-${num}`}
                          position="absolute"
                          top="0"
                          left={`${(num - 1) * 25}%`}
                          transform="translateX(-50%)"
                          fontSize="sm"
                          fontWeight={modalConfidencePoints === num ? "bold" : "medium"}
                          color={modalConfidencePoints === num ? TOP_BAR_BLUE : "gray.600"}
                        >
                          {num}
                        </Text>
                      ))}

                      <Slider
                        min={1}
                        max={5}
                        step={1}
                        value={modalConfidencePoints}
                        onChange={setModalConfidencePoints}
                      >
                        <SliderTrack>
                          <SliderFilledTrack bg={TOP_BAR_BLUE} />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>

                      {[1, 2, 3, 4, 5].map((num) => (
                        <Box
                          key={`tick-${num}`}
                          position="absolute"
                          top="calc(50% + 12px)"
                          left={`${(num - 1) * 25}%`}
                          transform="translate(-50%, -50%)"
                          w="2px"
                          h="12px"
                          bg="gray.500"
                          borderRadius="full"
                          pointerEvents="none"
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                {activeFixture?.odds && (
                  <Box>
                    <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={3}>
                      Game Odds
                    </Text>

                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      {[
                        { label: activeHomeTeam, value: activeFixture.odds.home_win },
                        { label: "Draw", value: activeFixture.odds.draw },
                        { label: activeAwayTeam, value: activeFixture.odds.away_win },
                      ].map(({ label, value }) => (
                        <Box
                          key={label}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={3}
                          textAlign="center"
                        >
                          <Text fontSize="xs" color="gray.500" mb={1} noOfLines={1}>
                            {label}
                          </Text>
                          <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} lineHeight="1.2">
                            {value ?? "—"}
                          </Text>
                          {value != null && (
                            <Text fontSize="xs" color="gray.500" mt={0.5}>
                              {toAmericanOdds(value)}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Grid>

                    <Text fontSize="xs" color="gray.400" mt={2}>
                      {activeFixture.odds.bookmaker}
                    </Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={3}>
                    Team Stats
                  </Text>

                  <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={{ base: 2, md: 4 }}>
                    <Box
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={{ base: 2, md: 4 }}
                      minW={0}
                    >
                      <Text fontWeight="bold" mb={{ base: 1, md: 2 }} noOfLines={1} fontSize="sm">
                        {activeHomeTeam}
                      </Text>
                      <Box overflowX="auto">
                        <Table size="xs" variant="simple" width="100%">
                          <Thead>
                            <Tr>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">W</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">D</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">L</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">F</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">A</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">GD</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">Pts</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.wins ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.draws ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.losses ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.goals_for ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.goals_against ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.goal_differential ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeHomeStanding?.points ?? 0}</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>

                    <Box
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={{ base: 2, md: 4 }}
                      minW={0}
                    >
                      <Text fontWeight="bold" mb={{ base: 1, md: 2 }} noOfLines={1} fontSize="sm">
                        {activeAwayTeam}
                      </Text>
                      <Box overflowX="auto">
                        <Table size="xs" variant="simple" width="100%">
                          <Thead>
                            <Tr>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">W</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">D</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">L</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">F</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">A</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">GD</Th>
                              <Th px={{ base: 1, md: 2 }} fontSize="0.6rem">Pts</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.wins ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.draws ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.losses ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.goals_for ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.goals_against ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.goal_differential ?? 0}</Td>
                              <Td px={{ base: 1, md: 2 }} fontSize="xs">{activeAwayStanding?.points ?? 0}</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  </Grid>
                </Box>

                <Box>
                  <MatchupHistoryContent
                    title="Matchup History"
                    teamA={activeFixture?.home_team}
                    teamB={activeFixture?.away_team}
                    fetcher={getPlMatchupHistory}
                    seasons={10}
                    weekPrefix="MW"
                    drawLabel="DRAW"
                    active={isPickModalOpen}
                    maxH="240px"
                  />
                </Box>

              </VStack>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      <ExpandedTableModal
        isOpen={isExpandedTableOpen}
        onClose={onExpandedTableClose}
        standingsRows={standingsRows}
      />

    </Box>
  );
}